import React, { useState, useEffect, useCallback } from 'react';
import { complaintsApi, staffApi, templatesApi, installationsApi, salesApi, inventoryApi } from '../api/client';
import CustomerSearch from '../components/CustomerSearch';
import { Badge, Modal, CID, Spin, Empty, useFlash, F, Confirm } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import { Edit, Trash, ChevRight, X } from '../components/Icons';

const openWA = (phone, msg) => {
  const n = String(phone || '').replace(/\D/g, '');
  const num = n.length === 10 ? `91${n}` : n;
  if (!num) return;
  window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
};

const fillTpl = (body, name, extras = {}) =>
  body
    .replace(/\[Customer Name\]/g,        name || 'Customer')
    .replace(/\[Complaint ID\]/g,         extras.complaintCode || extras.serviceId || '')
    .replace(/\[Service ID\]/g,           extras.serviceId || '')
    .replace(/\[Selected Preferred Date\]/g, extras.preferredDate || '')
    .replace(/\[Product\]/g,              extras.product || '');

const COMPLAINT_TYPES = ['Hardware Issue', 'No Power Output', 'Battery Drain', 'Noise / Beeping', 'Other'];
const STATUSES = [
  { value: 'open',        label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved',    label: 'Resolved' },
  { value: 'closed',      label: 'Closed' },
];

export default function Complaints() {
  const { canDelete } = useAuth();
  const [rows, setRows]       = useState([]);
  const [busy, setBusy]       = useState(true);
  const [staff, setStaff]     = useState([]);
  const [tpls, setTpls]       = useState([]);
  const { show, Flash }       = useFlash();

  // New complaint form
  const [showAdd, setAdd]     = useState(false);
  const [custType, setCustType] = useState('existing'); // 'existing' | 'walkin'
  const [form, setForm]       = useState({ customer_id:'',walk_in_name:'',walk_in_phone:'',complaint_type:'Hardware Issue',priority:'normal',description:'',assigned_to:'',preferred_date:'' });
  const [cFound, setCFound]   = useState(null);
  const [cInstalls, setCInstalls] = useState([]);
  const [saving, setSaving]   = useState(false); const [err, setErr] = useState('');

  // Search / filter
  const [found, setFound]     = useState(null);
  const [activeTab, setTab]   = useState('active'); // 'active' | 'closed'
  const [sortBy, setSortBy]   = useState('date_desc'); // sort option
  const [view, setView]       = useState('list');   // 'list' | 'calendar'
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const [selDay, setSelDay]   = useState(null); // 'YYYY-MM-DD'

  // Edit modal
  const [editing, setEditing] = useState(null);
  const [ef, setEF]           = useState({ assigned_to:'', status:'open', priority:'normal', resolution_notes:'', amount_received:'', payment_mode:'Cash' });
  const [editSaving, setES]   = useState(false); const [editErr, setEditErr] = useState('');

  // Replacement sale
  const [products, setProducts]       = useState([]);
  const [showSale, setShowSale]       = useState(false);
  const [saleItems, setSaleItems]     = useState([{ product_id:'', qty:1, unit_price:'' }]);
  const [saleDiscVal, setSaleDV]      = useState('');
  const [salePayMode, setSalePM]      = useState('Cash');
  const [salePayStatus, setSalePS]    = useState('paid');
  const [saleAmountPaid, setSaleAP]   = useState('');
  const [saleFollowup, setSaleFU]     = useState('');
  const [saleSaving, setSaleSaving]   = useState(false);

  // Delete
  const [del, setDel]         = useState(null);

  const set   = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setef = k => e => setEF(f => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await complaintsApi.list({ page_size: 200 }); setRows(data); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => {
    load();
    staffApi.list().then(({ data }) => setStaff(data.filter(s => s.role === 'field' || s.role === 'admin'))).catch(() => {});
    templatesApi.list().then(({ data }) => setTpls(data)).catch(() => {});
    inventoryApi.list({ page_size: 200 }).then(({ data }) => setProducts(data)).catch(() => {});
  }, [load]);


  const save = async () => {
    if (custType === 'existing' && !form.customer_id) { setErr('Please select a customer.'); return; }
    if (custType === 'walkin' && !form.walk_in_name.trim()) { setErr('Customer name is required.'); return; }
    if (!form.description) { setErr('Description is required.'); return; }
    setSaving(true); setErr('');
    const custName  = custType === 'walkin' ? form.walk_in_name : (cFound?.full_name || 'Customer');
    const custPhone = custType === 'walkin' ? form.walk_in_phone : (cFound?.phone || '');
    try {
      const payload = {
        complaint_type: form.complaint_type,
        priority: form.priority,
        description: form.description,
        assigned_to: form.assigned_to || undefined,
        preferred_date: form.preferred_date || undefined,
      };
      if (custType === 'existing') payload.customer_id = form.customer_id;
      else {
        payload.walk_in_name = form.walk_in_name.trim();
        if (form.walk_in_phone.trim()) payload.walk_in_phone = form.walk_in_phone.trim();
      }
      const { data } = await complaintsApi.create(payload);
      setRows(r => [data, ...r]);
      show(`Complaint ${data.complaint_code} created!`);

      const preferredDate = form.preferred_date
        ? new Date(form.preferred_date).toLocaleDateString('en-IN', { dateStyle: 'medium' })
        : '';
      const tpl = tpls.find(t => t.name.toLowerCase() === 'complaint confirmation')
        || tpls.find(t => t.name.toLowerCase().includes('complaint'));
      const msg = tpl
        ? fillTpl(tpl.body, custName, { complaintCode: data.complaint_code, serviceId: data.complaint_code, preferredDate })
        : `Dear ${custName}, your complaint (${data.complaint_code}) has been registered. Our team will contact you shortly. - Sakthi Solar Solutions`;
      if (custPhone) openWA(custPhone, msg);

      setAdd(false);
      setCustType('existing');
      setForm({ customer_id:'',walk_in_name:'',walk_in_phone:'',complaint_type:'Hardware Issue',priority:'normal',description:'',assigned_to:'',preferred_date:'' });
      setCFound(null); setCInstalls([]);
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(Array.isArray(d) ? d.map(x => x.msg).join(', ') : (d || 'Failed to create complaint.'));
    } finally { setSaving(false); }
  };

  const closeEdit = () => {
    setEditing(null); setShowSale(false);
    setSaleItems([{ product_id:'', qty:1, unit_price:'' }]);
    setSaleDV('');
    setSalePM('Cash'); setSalePS('paid'); setSaleAP(''); setSaleFU(''); setEditErr('');
  };

  const openEdit = c => {
    setEditing(c);
    setEF({ complaint_type: c.complaint_type, assigned_to: c.assigned_to || '', status: c.status, priority: c.priority, resolution_notes: c.resolution_notes || '', amount_received: c.amount_received ?? '', payment_mode: c.payment_mode || 'Cash' });
    setShowSale(false);
    setSaleItems([{ product_id:'', qty:1, unit_price:'' }]);
    setSaleDV('');
    setSalePM('Cash'); setSalePS('paid'); setSaleAP(''); setSaleFU(''); setEditErr('');
  };

  const setSaleItem = (idx, key, val) =>
    setSaleItems(s => s.map((it, i) => i === idx ? { ...it, [key]: val } : it));

  const saveEdit = async () => {
    setES(true); setEditErr('');
    try {
      const payload = {
        complaint_type:   ef.complaint_type,
        assigned_to:      ef.assigned_to || undefined,
        status:           ef.status,
        priority:         ef.priority,
        resolution_notes: ef.resolution_notes || undefined,
        payment_mode:     ef.payment_mode || undefined,
      };
      if (ef.amount_received !== '') payload.amount_received = Number(ef.amount_received);
      const { data } = await complaintsApi.update(editing.id, payload);
      setRows(r => r.map(x => x.id === data.id ? data : x));

      const validItems = saleItems.filter(i => i.product_id && i.unit_price !== '');
      if (showSale && editing.customer_id && validItems.length > 0) {
        setSaleSaving(true);
        const subtotal = validItems.reduce((s, i) => s + Number(i.unit_price) * (Number(i.qty) || 1), 0);
        const discPct = saleDiscVal && subtotal > 0
          ? (Number(saleDiscVal) / subtotal) * 100
          : 0;
        const salePayload = {
          customer_id:    editing.customer_id,
          payment_mode:   salePayMode,
          payment_status: salePayStatus,
          discount:       discPct,
          notes:          discPct > 0 ? 'Under Exchange' : undefined,
          items: validItems.map(i => ({
            product_id: i.product_id,
            qty:        Number(i.qty) || 1,
            unit_price: Number(i.unit_price),
          })),
        };
        if (salePayStatus === 'partial') {
          if (saleAmountPaid !== '') salePayload.amount_paid = Number(saleAmountPaid);
          if (saleFollowup) salePayload.followup_date = saleFollowup;
        }
        await salesApi.create(salePayload);
        show('Complaint updated & sale recorded!');
      } else {
        show('Complaint updated!');
      }
      closeEdit();
    } catch { setEditErr('Failed to update. Please try again.'); }
    finally { setES(false); setSaleSaving(false); }
  };

  const doDelete = async () => {
    try { await complaintsApi.delete(del.id); setRows(r => r.filter(x => x.id !== del.id)); show('Deleted.'); }
    catch { show('Delete failed.', 'error'); } finally { setDel(null); }
  };

  const staffName = id => staff.find(s => s.id === id)?.name || 'Unassigned';

  const PRIORITY_ORDER = { critical: 0, high: 1, normal: 2, low: 3 };

  const baseRows   = found ? rows.filter(r => r.customer_id === found.id) : rows;
  const activeRows = baseRows.filter(c => c.status === 'open' || c.status === 'in_progress');
  const closedRows = baseRows.filter(c => c.status === 'resolved' || c.status === 'closed');
  const rawDisplay = activeTab === 'active' ? activeRows : closedRows;
  const displayRows = [...rawDisplay].sort((a, b) => {
    if (sortBy === 'date_desc') return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === 'date_asc')  return new Date(a.created_at) - new Date(b.created_at);
    if (sortBy === 'priority')  return (PRIORITY_ORDER[a.priority] ?? 2) - (PRIORITY_ORDER[b.priority] ?? 2);
    if (sortBy === 'priority_asc') return (PRIORITY_ORDER[b.priority] ?? 2) - (PRIORITY_ORDER[a.priority] ?? 2);
    return 0;
  });

  if (busy) return <Spin/>;
  return (
    <div>
      <Flash/>
      <div className="search-row">
        <CustomerSearch
          onSelect={c => setFound(c)}
          placeholder="Filter by customer name / ID / phone…"
          disabled={!!found}
        />
        {found && <button className="btn btn-sm" onClick={() => setFound(null)}>Clear</button>}
        <button className="btn btn-primary" onClick={() => { setCustType('existing'); setForm({ customer_id:'',walk_in_name:'',walk_in_phone:'',complaint_type:'Hardware Issue',priority:'normal',description:'',assigned_to:'',preferred_date:'' }); setCFound(null); setCInstalls([]); setErr(''); setAdd(true); }}>+ New Complaint</button>
      </div>

      {found && (
        <div className="card" style={{ marginBottom: 12, padding: '12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <strong>{found.full_name}</strong> <CID code={found.customer_code}/>
            <div style={{ fontSize:12, color:'var(--text2)', marginTop:3 }}>{found.phone} · {found.district}</div>
          </div>
          <button className="btn btn-sm btn-primary" onClick={() => { setCFound(found); setForm(f => ({ ...f, customer_id: found.id })); setCustType('existing'); installationsApi.list(found.id).then(r => setCInstalls(r.data)).catch(() => setCInstalls([])); setAdd(true); }}>
            + New Complaint for this Customer
          </button>
        </div>
      )}

      {/* View toggle + Tabs */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8, borderBottom:'2px solid var(--border)' }}>
        <div style={{ display:'flex', gap:0 }}>
          {[
            { key:'active', label:'Active', count: activeRows.length },
            { key:'closed', label:'Resolved / Closed', count: closedRows.length },
          ].map(tab => (
            <button key={tab.key} onClick={() => setTab(tab.key)}
              style={{ background:'none', border:'none', padding:'8px 18px', cursor:'pointer', fontWeight: activeTab===tab.key ? 700 : 400,
                borderBottom: activeTab===tab.key ? '2px solid var(--primary)' : '2px solid transparent',
                color: activeTab===tab.key ? 'var(--primary)' : 'var(--text2)', marginBottom:-2, fontSize:13 }}>
              {tab.label}
              <span style={{ marginLeft:6, background: activeTab===tab.key ? 'var(--primary)' : 'var(--surface)', color: activeTab===tab.key ? '#fff' : 'var(--text2)',
                borderRadius:999, padding:'1px 7px', fontSize:11, fontWeight:600 }}>{tab.count}</span>
            </button>
          ))}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, paddingBottom:4 }}>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            style={{ fontSize:12, padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', cursor:'pointer', color:'var(--text)', background:'var(--bg)' }}
          >
            <option value="date_desc">Date Modified (Newest)</option>
            <option value="date_asc">Date Modified (Oldest)</option>
            <option value="priority">Priority (High → Low)</option>
            <option value="priority_asc">Priority (Low → High)</option>
          </select>
          {[['list','Log List'],['calendar','Calendar']].map(([v,lbl]) => (
            <button key={v} onClick={() => setView(v)}
              className={`btn btn-sm${view===v ? ' btn-primary' : ''}`}>{lbl}</button>
          ))}
        </div>
      </div>

      {/* ── Log List ── */}
      {view === 'list' && (
        <div className="card card-0">
          {displayRows.length === 0 ? <Empty msg={activeTab==='active' ? 'No active complaints' : 'No resolved or closed complaints'}/> : (
            <div className="tbl-wrap"><table>
              <thead>
                <tr><th>Date</th><th>ID</th><th>Customer</th><th>Type</th><th>Priority</th><th>Assigned To</th><th>Status</th><th>Amt Received</th><th>Payment Mode</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {displayRows.map(c => (
                  <tr key={c.id} style={{ cursor:'pointer' }} onClick={() => openEdit(c)}>
                    <td style={{ fontSize:12 }}>{new Date(c.created_at).toLocaleDateString('en-IN')}</td>
                    <td style={{ fontFamily:'monospace', fontSize:11 }}>{c.complaint_code}</td>
                    <td style={{ fontSize:12 }}>
                      {c.customer_name
                        ? <span>{c.customer_name}{c.walk_in_phone ? <span style={{ marginLeft:6, color:'var(--text2)' }}>({c.walk_in_phone})</span> : null}</span>
                        : <span style={{ color:'var(--text2)' }}>—</span>}
                    </td>
                    <td>{c.complaint_type}</td>
                    <td><Badge label={c.priority}/></td>
                    <td style={{ fontSize:12 }}>
                      {c.assigned_to
                        ? <span className="badge b-info">{staffName(c.assigned_to)}</span>
                        : <span className="badge b-gray">Unassigned</span>}
                    </td>
                    <td><Badge label={c.status}/></td>
                    <td style={{ fontWeight: c.amount_received ? 600 : 400, color: c.amount_received ? 'var(--green)' : 'var(--text2)' }}>
                      {c.amount_received != null ? `₹${Number(c.amount_received).toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td style={{ fontSize:12 }}>{c.payment_mode || '—'}</td>
                    <td onClick={e => e.stopPropagation()} style={{ display:'flex', gap:4 }}>
                      <button className="btn btn-sm" onClick={() => openEdit(c)} title="Edit"><Edit s={13}/></button>
                      {canDelete && <button className="btn btn-sm btn-danger" onClick={() => setDel(c)} title="Delete"><Trash s={13}/></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {/* ── Calendar ── */}
      {view === 'calendar' && (() => {
        const { year, month } = calMonth;
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const todayStr = new Date().toISOString().split('T')[0];

        // Map date string → complaints that day
        const byDay = {};
        baseRows.forEach(c => {
          const d = c.created_at?.split('T')[0];
          if (d) { if (!byDay[d]) byDay[d] = []; byDay[d].push(c); }
        });

        const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
        const cells = [];
        for (let i = 0; i < firstDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);

        const selDateStr = selDay;
        const selComplaints = selDateStr ? (byDay[selDateStr] || []) : [];

        const monthLabel = new Date(year, month).toLocaleDateString('en-IN', { month:'long', year:'numeric' });

        const prevMonth = () => setCalMonth(({ year:y, month:m }) => m === 0 ? { year:y-1, month:11 } : { year:y, month:m-1 });
        const nextMonth = () => setCalMonth(({ year:y, month:m }) => m === 11 ? { year:y+1, month:0 } : { year:y, month:m+1 });

        return (
          <div style={{ display:'grid', gridTemplateColumns: selDay ? '1fr 320px' : '1fr', gap:16, alignItems:'start' }}>
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              {/* Month nav */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px', borderBottom:'1px solid var(--border)' }}>
                <button className="btn btn-sm btn-ghost" onClick={prevMonth} style={{ padding:'4px 10px' }}>‹</button>
                <span style={{ fontWeight:700, fontSize:15, color:'var(--navy)' }}>{monthLabel}</span>
                <button className="btn btn-sm btn-ghost" onClick={nextMonth} style={{ padding:'4px 10px' }}>›</button>
              </div>
              {/* Day headers */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', borderBottom:'1px solid var(--border)' }}>
                {dayNames.map(d => (
                  <div key={d} style={{ textAlign:'center', padding:'8px 0', fontSize:11, fontWeight:700, color:'var(--text2)', textTransform:'uppercase' }}>{d}</div>
                ))}
              </div>
              {/* Day cells */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)' }}>
                {cells.map((day, idx) => {
                  if (!day) return <div key={`e${idx}`}/>;
                  const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                  const count   = byDay[dateStr]?.length || 0;
                  const isToday = dateStr === todayStr;
                  const isSel   = dateStr === selDay;
                  return (
                    <div key={dateStr} onClick={() => setSelDay(isSel ? null : dateStr)}
                      style={{
                        minHeight:70, padding:'8px 6px', borderRight:'1px solid var(--border)', borderBottom:'1px solid var(--border)',
                        cursor: count ? 'pointer' : 'default',
                        background: isSel ? 'rgba(59,130,246,.08)' : 'transparent',
                        transition:'background .1s',
                      }}>
                      <div style={{
                        width:26, height:26, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:13, fontWeight: isToday ? 700 : 400,
                        background: isToday ? 'var(--blue)' : 'transparent',
                        color: isToday ? '#fff' : 'var(--text)',
                        marginBottom:4,
                      }}>{day}</div>
                      {count > 0 && (
                        <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                          {byDay[dateStr].slice(0,3).map(c => (
                            <span key={c.id} style={{
                              fontSize:10, fontWeight:600, padding:'1px 5px', borderRadius:4,
                              background: c.priority==='critical' ? '#fee2e2' : c.priority==='high' ? '#fef3c7' : '#dbeafe',
                              color:      c.priority==='critical' ? '#991b1b'  : c.priority==='high' ? '#92400e'  : '#1e40af',
                              maxWidth:72, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                            }}>{c.complaint_type}</span>
                          ))}
                          {count > 3 && <span style={{ fontSize:10, color:'var(--text2)', fontWeight:600 }}>+{count-3}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Day detail panel */}
            {selDay && (
              <div className="card" style={{ padding:0, overflow:'hidden' }}>
                <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontWeight:700, fontSize:13, color:'var(--navy)' }}>
                    {new Date(selDay+'T00:00:00').toLocaleDateString('en-IN', { dateStyle:'long' })}
                  </span>
                  <button className="btn btn-sm btn-ghost" style={{ padding:'2px 6px' }} onClick={() => setSelDay(null)}><X s={13}/></button>
                </div>
                {selComplaints.length === 0
                  ? <div style={{ padding:20, fontSize:13, color:'var(--text2)' }}>No complaints on this date.</div>
                  : <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
                      {selComplaints.map((c, i) => (
                        <div key={c.id} onClick={() => openEdit(c)}
                          style={{ padding:'12px 16px', borderBottom: i < selComplaints.length-1 ? '1px solid var(--border)' : 'none', cursor:'pointer' }}>
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
                            <span style={{ fontFamily:'monospace', fontSize:11, color:'var(--navy)', fontWeight:700 }}>{c.complaint_code}</span>
                            <Badge label={c.status}/>
                          </div>
                          <div style={{ fontSize:12, fontWeight:600, marginBottom:2 }}>{c.complaint_type}</div>
                          <div style={{ fontSize:11, color:'var(--text2)' }}>
                            {c.customer_name || '—'}
                            {c.walk_in_phone ? ` · ${c.walk_in_phone}` : ''}
                          </div>
                          {c.description && <div style={{ fontSize:11, color:'var(--text2)', marginTop:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.description}</div>}
                        </div>
                      ))}
                    </div>
                }
              </div>
            )}
          </div>
        );
      })()}

      {/* ── New Complaint Modal ── */}
      <Modal open={showAdd} onClose={() => { setAdd(false); setCustType('existing'); setCFound(null); setCInstalls([]); setForm({ customer_id:'',walk_in_name:'',walk_in_phone:'',complaint_type:'Hardware Issue',priority:'normal',description:'',assigned_to:'',preferred_date:'' }); setErr(''); }} title="New Complaint"
        footer={
          <>
            <button className="btn" onClick={() => { setAdd(false); setCustType('existing'); setCFound(null); setCInstalls([]); setForm({ customer_id:'',walk_in_name:'',walk_in_phone:'',complaint_type:'Hardware Issue',priority:'normal',description:'',assigned_to:'',preferred_date:'' }); setErr(''); }}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Submit Complaint'}</button>
          </>
        }
      >
        {err && <div className="notice n-err" style={{ marginBottom:10 }}>{err}</div>}

        {/* Customer type toggle */}
        <div style={{ display:'flex', gap:8, marginBottom:14 }}>
          <button
            className={`btn btn-sm${custType === 'existing' ? ' btn-primary' : ''}`}
            onClick={() => { setCustType('existing'); setCFound(null); setCInstalls([]); setForm(f => ({ ...f, customer_id:'', walk_in_name:'', walk_in_phone:'' })); }}
          >Existing Customer</button>
          <button
            className={`btn btn-sm${custType === 'walkin' ? ' btn-primary' : ''}`}
            onClick={() => { setCustType('walkin'); setCFound(null); setCInstalls([]); setForm(f => ({ ...f, customer_id:'' })); }}
          >Walk-in / New</button>
        </div>

        {/* Existing customer picker */}
        {custType === 'existing' && (
          !form.customer_id
            ? <div style={{ marginBottom:12 }}>
                <CustomerSearch onSelect={c => {
                  setCFound(c);
                  setForm(f => ({ ...f, customer_id: c.id }));
                  installationsApi.list(c.id).then(r => setCInstalls(r.data)).catch(() => setCInstalls([]));
                }}/>
              </div>
            : <>
                <div style={{ background:'var(--surface)', borderRadius:8, padding:'12px 14px', marginBottom:10 }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                    <div>
                      <strong style={{ fontSize:15 }}>{cFound?.full_name}</strong>
                      {cFound?.customer_code && <CID code={cFound.customer_code}/>}
                    </div>
                    <button className="btn btn-sm btn-ghost" style={{ padding:'2px 8px' }}
                      onClick={() => { setCFound(null); setCInstalls([]); setForm(f => ({ ...f, customer_id:'' })); }}>
                      <X s={13}/>
                    </button>
                  </div>
                  <div style={{ fontSize:12, color:'var(--text2)', display:'flex', flexDirection:'column', gap:3 }}>
                    {cFound?.phone && <span>📞 {cFound.phone}</span>}
                    {cFound?.address && <span>📍 {cFound.address}{cFound.district ? `, ${cFound.district}` : ''}{cFound.state ? `, ${cFound.state}` : ''}{cFound.pincode ? ` — ${cFound.pincode}` : ''}</span>}
                  </div>
                  {cInstalls.length > 0 && (
                    <div style={{ marginTop:10, borderTop:'1px solid var(--border)', paddingTop:8 }}>
                      <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', color:'var(--text2)', marginBottom:6 }}>Installed Products</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        {cInstalls.map(ins => (
                          <div key={ins.id} style={{ fontSize:12, display:'flex', gap:10, alignItems:'center' }}>
                            <span style={{ fontWeight:500 }}>{ins.product_name}</span>
                            <span style={{ color:'var(--text2)' }}>{ins.installation_date}</span>
                            {ins.installed_by && <span style={{ color:'var(--text2)' }}>by {ins.installed_by}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
        )}

        {/* Walk-in customer fields */}
        {custType === 'walkin' && (
          <div className="fg-2" style={{ marginBottom:12 }}>
            <F label="Customer Name" req>
              <input value={form.walk_in_name} onChange={set('walk_in_name')} placeholder="Enter customer name"/>
            </F>
            <F label="Phone Number">
              <input value={form.walk_in_phone} onChange={set('walk_in_phone')} placeholder="10-digit phone"/>
            </F>
          </div>
        )}

        <div className="fg-2">
          <F label="Complaint Type">
            <select value={form.complaint_type} onChange={set('complaint_type')}>
              {COMPLAINT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </F>
          <F label="Priority">
            <select value={form.priority} onChange={set('priority')}>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </F>
          <F label="Description" req full>
            <textarea value={form.description} onChange={set('description')} placeholder="Describe the issue in detail…"/>
          </F>
          <F label="Assign To (Field Staff)">
            <select value={form.assigned_to} onChange={set('assigned_to')}>
              <option value="">Unassigned</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
            </select>
          </F>
          <F label="Preferred Visit Date">
            <input type="date" value={form.preferred_date} onChange={set('preferred_date')}/>
          </F>
        </div>
      </Modal>

      {/* ── Edit / Update Complaint Modal ── */}
      <Modal open={!!editing} onClose={closeEdit} title={`Complaint — ${editing?.complaint_code}`}
        footer={
          <>
            <button className="btn" onClick={closeEdit}>Close</button>
            <button className="btn btn-primary" onClick={saveEdit} disabled={editSaving || saleSaving}>
              {editSaving || saleSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </>
        }
      >
        {editErr && <div className="notice n-err" style={{ marginBottom:10 }}>{editErr}</div>}
        {editing && (
          <>
            {/* Read-only info */}
            <div style={{ background:'var(--surface)', borderRadius:8, padding:'12px 14px', marginBottom:14, fontSize:13 }}>
              <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                <span><span style={{ color:'var(--text2)', fontSize:11, fontWeight:600, textTransform:'uppercase' }}>Raised</span><div style={{ marginTop:2 }}>{new Date(editing.created_at).toLocaleDateString('en-IN', { dateStyle:'medium' })}</div></span>
                {editing.preferred_date && <span><span style={{ color:'var(--text2)', fontSize:11, fontWeight:600, textTransform:'uppercase' }}>Preferred Date</span><div style={{ marginTop:2 }}>{editing.preferred_date}</div></span>}
                {editing.customer_name && <span><span style={{ color:'var(--text2)', fontSize:11, fontWeight:600, textTransform:'uppercase' }}>Customer</span><div style={{ marginTop:2 }}>{editing.customer_name}{editing.walk_in_phone ? ` · ${editing.walk_in_phone}` : ''}</div></span>}
              </div>
              {editing.description && <div style={{ marginTop:10, color:'var(--text2)', fontSize:12, lineHeight:1.5 }}>{editing.description}</div>}
            </div>

            <div className="fg-2">
              <F label="Complaint Type">
                <select value={ef.complaint_type} onChange={setef('complaint_type')}>
                  {COMPLAINT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </F>
              <F label="Status">
                <select value={ef.status} onChange={setef('status')}>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </F>
              <F label="Priority">
                <select value={ef.priority} onChange={setef('priority')}>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </F>
              <F label="Assign To (Staff)" full>
                <select value={ef.assigned_to} onChange={setef('assigned_to')}>
                  <option value="">Unassigned</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                </select>
              </F>
              <F label="Remarks / Resolution Notes" full>
                <textarea value={ef.resolution_notes} onChange={setef('resolution_notes')}
                  placeholder="Enter remarks, resolution details, or reason for closure…"/>
              </F>
              <F label="Amount Received (₹)">
                <input type="number" min="0" placeholder="0 if free / warranty"
                  value={ef.amount_received} onChange={setef('amount_received')}/>
              </F>
              <F label="Payment Mode">
                <select value={ef.payment_mode} onChange={setef('payment_mode')}>
                  {['Cash','Card','UPI','Bank Transfer','Free / Warranty'].map(m => <option key={m}>{m}</option>)}
                </select>
              </F>
            </div>

            {/* ── Replacement / Parts Sale ── only for registered customers */}
            {editing.customer_id && (
              <div style={{ marginTop:18, borderTop:'1px solid var(--border)', paddingTop:14 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: showSale ? 14 : 0 }}>
                  <div>
                    <span style={{ fontSize:13, fontWeight:600 }}>Replacement / Parts Sale</span>
                    <span style={{ marginLeft:8, fontSize:11, color:'var(--text2)' }}>Record a product sale linked to this complaint</span>
                  </div>
                  <button className={`btn btn-sm${showSale ? '' : ' btn-primary'}`}
                    onClick={() => setShowSale(s => { if (s) setSaleItems([{ product_id:'', qty:1, unit_price:'' }]); return !s; })}>
                    {showSale ? 'Remove' : '+ Add Sale'}
                  </button>
                </div>

                {showSale && (
                  <>
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {saleItems.map((item, idx) => {
                        const lineTotal = item.product_id && item.unit_price !== '' ? Number(item.unit_price) * Number(item.qty || 1) : 0;
                        return (
                          <div key={idx} style={{ display:'grid', gridTemplateColumns:'1fr 70px 110px auto', gap:6, alignItems:'center' }}>
                            <select value={item.product_id} onChange={e => {
                              const p = products.find(p => p.id === e.target.value);
                              setSaleItem(idx, 'product_id', e.target.value);
                              if (p && !item.unit_price) setSaleItem(idx, 'unit_price', String(p.mrp));
                            }}>
                              <option value="">Select product…</option>
                              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                            <input type="number" min="1" value={item.qty} placeholder="Qty"
                              onChange={e => setSaleItem(idx, 'qty', e.target.value)}/>
                            <input type="number" min="0" value={item.unit_price} placeholder="Unit price ₹"
                              onChange={e => setSaleItem(idx, 'unit_price', e.target.value)}/>
                            <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                              {lineTotal > 0 && <span style={{ fontSize:12, color:'var(--text2)', whiteSpace:'nowrap' }}>₹{lineTotal.toLocaleString('en-IN')}</span>}
                              {saleItems.length > 1 && (
                                <button className="btn btn-sm btn-danger" style={{ padding:'2px 7px' }}
                                  onClick={() => setSaleItems(s => s.filter((_, i) => i !== idx))}>×</button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <button className="btn btn-sm" style={{ marginTop:8 }}
                      onClick={() => setSaleItems(s => [...s, { product_id:'', qty:1, unit_price:'' }])}>
                      + Add Item
                    </button>

                    {/* Discount — Under Exchange */}
                    <div style={{ marginTop:14, padding:'10px 12px', background:'var(--surface)', borderRadius:8, border:'1px solid var(--border)' }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', marginBottom:8 }}>Under Exchange Discount (optional)</div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ padding:'5px 14px', fontSize:13, fontWeight:600, borderRadius:6, background:'var(--primary)', color:'#fff', whiteSpace:'nowrap' }}>₹ Fixed</span>
                        <input type="number" min="0" step="0.01" value={saleDiscVal}
                          onChange={e => setSaleDV(e.target.value)}
                          placeholder="0.00" style={{ width:120 }}/>
                        {saleDiscVal > 0 && <span style={{ fontSize:12, color:'var(--green)', fontWeight:600 }}>
                          − ₹{Number(saleDiscVal).toLocaleString('en-IN')} off
                        </span>}
                      </div>
                    </div>

                    {/* Sale totals + payment */}
                    {saleItems.some(i => i.product_id && i.unit_price !== '') && (() => {
                      const subtotal = saleItems.reduce((sum, i) => sum + (i.product_id && i.unit_price !== '' ? Number(i.unit_price) * Number(i.qty || 1) : 0), 0);
                      const discAmt = saleDiscVal ? Math.min(Number(saleDiscVal), subtotal) : 0;
                      const grandTotal = subtotal - discAmt;
                      return (
                        <div style={{ marginTop:12, background:'var(--surface)', borderRadius:8, padding:'10px 14px', fontSize:13 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}><span style={{ color:'var(--text2)' }}>Subtotal</span><span>₹{subtotal.toLocaleString('en-IN')}</span></div>
                          {discAmt > 0 && <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, color:'var(--green)' }}><span>Under Exchange</span><span>− ₹{discAmt.toLocaleString('en-IN', { maximumFractionDigits:2 })}</span></div>}
                          <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, marginTop:4, borderTop:'1px solid var(--border)', paddingTop:4 }}><span>Total</span><span>₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits:2 })}</span></div>
                        </div>
                      );
                    })()}

                    <div className="fg-2" style={{ marginTop:12 }}>
                      <F label="Payment Mode">
                        <select value={salePayMode} onChange={e => setSalePM(e.target.value)}>
                          {['Cash','Card','UPI','Bank Transfer'].map(m => <option key={m}>{m}</option>)}
                        </select>
                      </F>
                      <F label="Payment Status">
                        <select value={salePayStatus} onChange={e => { setSalePS(e.target.value); setSaleAP(''); }}>
                          <option value="paid">Fully Paid</option>
                          <option value="partial">Part Payment</option>
                          <option value="pending">Not Yet Paid</option>
                        </select>
                      </F>
                      {salePayStatus === 'partial' && (<>
                        <F label="Amount Paid Now (₹)" req>
                          <input type="number" min="0" step="0.01"
                            value={saleAmountPaid} onChange={e => setSaleAP(e.target.value)}
                            placeholder="Amount received so far"/>
                        </F>
                        <F label="Follow-up Date">
                          <input type="date" value={saleFollowup} onChange={e => setSaleFU(e.target.value)}/>
                        </F>
                      </>)}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </Modal>

      <Confirm open={!!del} onClose={() => setDel(null)} onOk={doDelete}
        msg={`Delete complaint ${del?.complaint_code}? This cannot be undone.`}/>
    </div>
  );
}
